/**
 * Susurro pairing code (QR payload + copyable text).
 *
 * Guest-first: to write to someone their **device id** suffices (public
 * routing; the content always goes E2E and the relay never sees the plaintext). The QR/code
 * encodes that id + the purpose (start a chat or link a new device).
 * The chat safety number still covers anti-MITM verification.
 *
 * `build`/`parse` are PURE and testable (unit); the QR render lives in
 * `components/ui/qr-code.tsx` and the real camera scan is manual validation.
 */

/* oxlint-disable lingui/no-unlocalized-strings -- wire format (prefix/regex), not UI */

type PairingKind = "chat" | "link";

interface PairingPayload {
  readonly kind: PairingKind;
  readonly deviceId: string;
}

const PREFIX = "SUSURRO1";
/** Device ids: `dev-` + base64url-ish chars (see persistence.ts). */
const DEVICE_ID = "dev-[A-Za-z0-9_-]{3,}";
const CODE_RE = new RegExp(`${PREFIX}:(chat|link):(${DEVICE_ID})`);
const BARE_RE = new RegExp(`^(${DEVICE_ID})$`);

/** Builds the code to show (QR + copyable): `SUSURRO1:chat:dev-xxxx`. */
export function buildPairingCode(payload: PairingPayload): string {
  return `${PREFIX}:${payload.kind}:${payload.deviceId}`;
}

/**
 * Parses a scanned/pasted code. Tolerates it coming wrapped (text/URL with the
 * code inside) and also accepts a "bare" device id (→ start chat).
 */
export function parsePairingCode(scanned: string): PairingPayload | undefined {
  const text = scanned.trim();
  const m = CODE_RE.exec(text);
  if (m) {
    return { kind: m[1] === "link" ? "link" : "chat", deviceId: m[2]! };
  }
  const bare = BARE_RE.exec(text);
  return bare ? { kind: "chat", deviceId: bare[1]! } : undefined;
}
